import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import CardTitle from "@saleor/components/CardTitle";
import Grid from "@saleor/components/Grid";
import { BannerErrorFragment } from "@saleor/fragments/types/BannerErrorFragment";
import { ChangeEvent } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { getFormErrors } from "@saleor/utils/errors";
import getBannerErrorMessage from "@saleor/utils/errors/banner";
import classNames from "classnames";
import Moment from "moment";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { BannerDetailsFormData } from "../BannerDetailsPage";

const useStyles = makeStyles(
  () => ({
    card: {
      width: "63%"
    },
    titleBar: {
      "& span": {
        lineBreak: "anywhere",
        flex: "none",
        width: "-webkit-fill-available"
      }
    },
    rows: {
      display: "flex",
      height: "204px",
      flexDirection: "column",
      justifyContent: "space-around"
    },
    lastColumn: {
      paddingBottom: "25px"
    }
  }),
  { name: "BannerDateTime" }
);
interface BannerDateTimeProps {
  clientErrors?: {
    endDate?: string;
    endTime?: string;
    startDate?: string;
    startTime?: string;
  };
  data: BannerDetailsFormData;
  updatePage?: boolean;
  disabled: boolean;
  errors?: BannerErrorFragment[];
  onChange: (event: ChangeEvent<any>) => void;
}

const BannerDateTime = ({
  data,
  disabled,
  errors,
  clientErrors,
  updatePage,
  onChange
}: BannerDateTimeProps) => {
  const classes = useStyles({});
  const notify = useNotifier();
  const intl = useIntl();
  const formErrors = getFormErrors(["startDate", "endDate"], errors || []);

  const [hasError, setHasError] = useState(false);

  const handleDate = (
    value: any,
    dateName,
    onChange: (event: ChangeEvent<any>) => void
  ) => {
    let dateA;
    let dateB;
    let newTimeName;
    setHasError(false);
    if (dateName === "startDate") {
      dateA = value.target.value;
      dateB = data?.endDate;
      newTimeName = "startTime";
      onChange({
        target: {
          name: "startTime",
          value: "00:00"
        }
      });
    }

    if (dateName === "endDate") {
      dateA = data?.startDate;
      dateB = value.target.value;
      newTimeName = "endTime";
      onChange({
        target: {
          name: "endTime",
          value: "23:59"
        }
      });
    }
    if (dateA && dateB) {
      if (dateA === dateB) {
        const datetimeA = Moment(dateA + " " + data?.startTime);
        const datetimeB = Moment(dateB + " " + data?.endTime);

        if (data.startTime && data.endTime && !datetimeB.isAfter(datetimeA)) {
          notify({
            status: "error",
            text: intl.formatMessage(commonMessages.timeError)
          });
          onChange({
            target: {
              name: newTimeName,
              value: ""
            }
          });
          setHasError(true);
        }
      }
    }
    if (value && !hasError) {
      onChange({
        target: {
          name: dateName,
          value: value.target.value
        }
      });
      setHasError(false);
    }
  };

  const handleTime = (
    value: any,
    timerName,
    onChange: (event: ChangeEvent<any>) => void
  ) => {
    let datetimeA;
    let datetimeB;
    setHasError(false);
    if (data.startDate && data.endDate) {
      if (data.startDate === data.endDate) {
        if (timerName === "startTime") {
          datetimeA = Moment(data.startDate + " " + value.target.value);
          datetimeB = Moment(data.endDate + " " + data.endTime);
        }

        if (timerName === "endTime") {
          datetimeA = Moment(data.startDate + " " + data.startTime);
          datetimeB = Moment(data.endDate + " " + value.target.value);
        }

        if (data.startTime && data.endTime && !datetimeB.isAfter(datetimeA)) {
          notify({
            status: "error",
            text: intl.formatMessage(commonMessages.timeError)
          });
          onChange({
            target: {
              name: timerName,
              value: ""
            }
          });
          setHasError(true);
        }
      }
    }
    if (value && !hasError) {
      onChange({
        target: {
          name: timerName,
          value: value.target.value
        }
      });
      setHasError(false);
    }
  };
  return (
    <Card className={classNames({ [classes.card]: updatePage })}>
      <CardTitle
        className={classes.titleBar}
        title={intl.formatMessage({
          defaultMessage: "Active Dates",
          description: "time during discount is active, header"
        })}
      />
      <CardContent className={classes.rows}>
        <Grid variant="uniform">
          <TextField
            disabled={disabled}
            error={!!formErrors.startDate || !!clientErrors?.startDate}
            helperText={
              getBannerErrorMessage(formErrors.startDate, intl) ||
              clientErrors?.startDate
            }
            name={"startDate" as keyof FormData}
            onKeyPress={event => {
              if (event.key) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onChange={value => handleDate(value, "startDate", onChange)}
            label={intl.formatMessage(commonMessages.startDate)}
            value={data?.startDate}
            type="date"
            inputProps={{
              max: data?.endDate,
              min: Moment(new Date()).format("YYYY-MM-DD")
            }}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.startDate || !!clientErrors?.startTime}
            helperText={
              getBannerErrorMessage(formErrors.startDate, intl) ||
              clientErrors?.startTime
            }
            name={"startTime" as keyof FormData}
            onChange={value => handleTime(value, "startTime", onChange)}
            onKeyPress={event => {
              if (event.key) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            label={intl.formatMessage(commonMessages.startHour)}
            value={data?.startTime}
            type="time"
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <br />
        <Grid variant="uniform" className={classes.lastColumn}>
          <TextField
            disabled={disabled}
            error={!!formErrors.endDate || !!clientErrors?.endDate}
            helperText={
              getBannerErrorMessage(formErrors.endDate, intl) ||
              clientErrors?.endDate
            }
            name={"endDate" as keyof FormData}
            onChange={value => handleDate(value, "endDate", onChange)}
            onKeyPress={event => {
              if (event.key) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            label={intl.formatMessage(commonMessages.endDate)}
            value={data?.endDate}
            type="date"
            inputProps={{
              min: data?.startDate
                ? data.startDate
                : Moment(new Date())
                    .add(1, "d")
                    .format("YYYY-MM-DD")
            }}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
          <TextField
            disabled={disabled}
            error={!!formErrors.endDate || !!clientErrors?.endTime}
            helperText={
              getBannerErrorMessage(formErrors.endDate, intl) ||
              clientErrors?.endTime
            }
            name={"endTime" as keyof FormData}
            onChange={value => handleTime(value, "endTime", onChange)}
            onKeyPress={event => {
              if (event.key) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            label={intl.formatMessage(commonMessages.endHour)}
            value={data?.endTime}
            type="time"
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
      </CardContent>
    </Card>
  );
};
export default BannerDateTime;
